import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";

import {
  Button,
  Input,
  Textarea,
  SectionHeader,
} from "../../../../../design-system";
import {
  EmailAutomationStatus,
  MiloEmailStatus,
  WelcomeExperimentResults,
  WelcomeStep,
  SequenceType,
  getEmailAutomationStatus,
  setupEmailAutomation,
  verifyEmailDomain,
  getEmailSequence,
  getMiloEmailStatus,
  getWelcomeExperimentResults,
  resumeMiloEmailSending,
  saveEmailSequence,
  sendEmailCampaignNow,
  sendDraftEmailCampaign,
  sendTestEmail,
  ImportEmailSubscriber,
  importEmailSubscribers,
} from "../../../../../database/email/emailAutomationApi";
import {
  CampaignSummary,
  listEmailCampaigns,
  deleteEmailCampaign,
} from "../../../../../database/email/emailCampaignApi";

type Props = {
  communityId?: string;
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-6);
  margin-bottom: var(--space-6);
  background: var(--color-surface);
`;

const Intro = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0;
  max-width: 720px;
`;

const MiloCard = styled.section<{ paused?: boolean }>`
  border: 1px solid
    ${({ paused }) =>
      paused
        ? "var(--color-danger, #d1242f)"
        : "var(--color-border-strong, var(--color-border))"};
  border-radius: var(--radius-md);
  padding: var(--space-5);
  background: var(--color-surface-subtle);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const MiloHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const MiloMonogram = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  background: var(--color-text);
  color: var(--color-surface);
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold, 600);
  letter-spacing: 0.08em;
`;

const MiloIdentity = styled.div`
  min-width: 0;
  flex: 1;

  strong {
    display: block;
    color: var(--color-text);
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
  }

  span {
    display: block;
    color: var(--color-text-secondary);
    font-family: var(--font-sans);
    font-size: var(--font-size-xs);
    margin-top: 2px;
  }
`;

const MiloPill = styled.span<{ tone?: "good" | "warn" | "neutral" }>`
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: 3px 9px;
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: ${({ tone }) =>
    tone === "good"
      ? "var(--color-success, #1a7f37)"
      : tone === "warn"
      ? "var(--color-danger, #d1242f)"
      : "var(--color-text-secondary)"};
  background: var(--color-surface);
  white-space: nowrap;
`;

const MiloGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-3);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const MiloMetric = styled.div`
  border-left: 2px solid var(--color-border-strong, var(--color-border));
  padding-left: var(--space-3);
  min-width: 0;

  span {
    display: block;
    color: var(--color-text-secondary);
    font-family: var(--font-sans);
    font-size: 11px;
    line-height: 1.35;
  }

  strong {
    display: block;
    color: var(--color-text);
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const MiloCopy = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  line-height: 1.55;
`;

const Row = styled.div`
  display: flex;
  gap: var(--space-3);
  align-items: flex-end;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  flex: 1;
  min-width: 240px;
`;

const Label = styled.label`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

const Dot = styled.span<{ on: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ on }) =>
    on ? "var(--color-success, #1a7f37)" : "var(--color-warning-text, #9a6700)"};
`;

const Muted = styled.span`
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
`;

const Warn = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-warning-text, #9a6700);
`;

const Notice = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-success, #1a7f37);
`;

const ToggleButton = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-decoration: underline;
`;

const ImportLabel = styled.label<{ $disabled?: boolean }>`
  align-self: flex-start;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-decoration: underline;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};

  input {
    display: none;
  }
`;

const DnsBox = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  background: var(--color-surface-subtle);
`;

const DnsTable = styled.div`
  overflow-x: auto;
  font-family: var(--font-mono, monospace);
  font-size: var(--font-size-xs);
  color: var(--color-text);

  table {
    border-collapse: collapse;
    width: 100%;
  }
  th,
  td {
    text-align: left;
    padding: var(--space-1) var(--space-3);
    border-bottom: 1px solid var(--color-border);
    max-width: 380px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StepCard = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const StepHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
`;

const SmallInput = styled(Input)`
  max-width: 90px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
`;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto auto auto;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-3) var(--space-4);
  border-top: 1px solid var(--color-border);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);

  &:first-child {
    border-top: none;
  }
`;

const Subject = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusTag = styled.span<{ sent: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
  color: ${({ sent }) =>
    sent ? "var(--color-success, #1a7f37)" : "var(--color-text-secondary)"};
  background: var(--color-surface-subtle);
`;

const Empty = styled.div`
  padding: var(--space-5);
  text-align: center;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const DeleteButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 2px 6px;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);

  &:hover {
    color: var(--color-danger, #d1242f);
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const RowSendButton = styled.button`
  appearance: none;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-accent-text, var(--color-text));

  &:hover {
    border-color: var(--color-accent, var(--color-border-strong));
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Confirm = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
  border: 1px solid var(--color-border);
  background: var(--color-surface-subtle);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

const fmtCampaignDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const fmtPercent = (value?: number | null) =>
  `${Math.round((Number(value) || 0) * 1000) / 10}%`;

const fmtMiloStatus = (value?: string | null) =>
  String(value || "waiting")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

// Root of the Tribed-provided sender subdomains. Must match TRIBED_MAIL_ROOT
// in functions/src/emailAutomation.ts.
const TRIBED_MAIL_ROOT = "tribedemail.com";

const toLabel = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);

const IMPORT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const normalizeHeader = (value: unknown) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");

const parseSubscriberFile = async (
  file: File
): Promise<ImportEmailSubscriber[]> => {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!firstSheet) return [];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(firstSheet, {
    header: 1,
    defval: "",
    blankrows: false,
  });
  if (!rows.length) return [];

  const headers = rows[0].map(normalizeHeader);
  const emailAliases = new Set([
    "email",
    "emailaddress",
    "emailadresse",
    "correo",
    "correoelectronico",
    "mail",
  ]);
  let emailIndex = headers.findIndex((header) => emailAliases.has(header));
  let dataStart = 1;

  // Some provider exports have no header. In that case, detect the column
  // containing the most email addresses and include the first row as data.
  if (emailIndex < 0) {
    const width = Math.max(...rows.slice(0, 30).map((row) => row.length));
    let bestScore = 0;
    for (let column = 0; column < width; column++) {
      const score = rows
        .slice(0, 30)
        .filter((row) => IMPORT_EMAIL_RE.test(String(row[column] || "").trim()))
        .length;
      if (score > bestScore) {
        bestScore = score;
        emailIndex = column;
      }
    }
    dataStart = 0;
  }
  if (emailIndex < 0) return [];

  const fullNameIndex = headers.findIndex((header) =>
    ["name", "fullname", "contactname", "subscribername", "nombre"].includes(
      header
    )
  );
  const firstNameIndex = headers.findIndex((header) =>
    ["firstname", "givenname", "nombre"].includes(header)
  );
  const lastNameIndex = headers.findIndex((header) =>
    ["lastname", "surname", "familyname", "apellido"].includes(header)
  );
  const deduped = new Map<string, ImportEmailSubscriber>();
  rows.slice(dataStart).forEach((row) => {
    const email = String(row[emailIndex] || "").trim().toLowerCase();
    if (!IMPORT_EMAIL_RE.test(email) || deduped.has(email)) return;
    const name =
      fullNameIndex >= 0
        ? String(row[fullNameIndex] || "").trim()
        : [firstNameIndex, lastNameIndex]
            .filter((index) => index >= 0)
            .map((index) => String(row[index] || "").trim())
            .filter(Boolean)
            .join(" ");
    deduped.set(email, { email, ...(name ? { name } : {}) });
  });
  return Array.from(deduped.values());
};

const EmailAutomationPanel = ({ communityId }: Props) => {
  const [status, setStatus] = useState<EmailAutomationStatus | null>(null);
  const [milo, setMilo] = useState<MiloEmailStatus | null>(null);
  const [miloResults, setMiloResults] =
    useState<WelcomeExperimentResults | null>(null);
  const [resumingMilo, setResumingMilo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Setup / sender settings
  const [useTribedDomain, setUseTribedDomain] = useState(true);
  const [localPart, setLocalPart] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [dailyLimit, setDailyLimit] = useState("");
  const [postalAddress, setPostalAddress] = useState("");
  const [showSetupAdvanced, setShowSetupAdvanced] = useState(false);
  const [showSenderSettings, setShowSenderSettings] = useState(false);
  const [savingSetup, setSavingSetup] = useState(false);
  const [checkingDomain, setCheckingDomain] = useState(false);

  // Sequence editor (welcome or winback)
  const [seqType, setSeqType] = useState<SequenceType | null>(null);
  const [steps, setSteps] = useState<WelcomeStep[]>([]);
  const [savingSequence, setSavingSequence] = useState(false);
  const [winbackEnabled, setWinbackEnabled] = useState(true);
  const [inactiveDays, setInactiveDays] = useState("30");
  const [cooldownDays, setCooldownDays] = useState("90");
  const [testing, setTesting] = useState(false);

  // Composer + history
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const refreshCampaigns = useCallback(async () => {
    if (!communityId) return;
    try {
      const res = await listEmailCampaigns(communityId);
      setCampaigns(res.campaigns || []);
    } catch {
      // Non-fatal: the composer still works without the history list.
    }
  }, [communityId]);

  const refresh = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError(null);
    try {
      const [res, miloStatus] = await Promise.all([
        getEmailAutomationStatus(communityId),
        getMiloEmailStatus(communityId).catch(() => null),
      ]);
      setStatus(res);
      setMilo(miloStatus);
      if (miloStatus?.currentExperiment?.id) {
        const experimentResults = await getWelcomeExperimentResults(
          communityId,
          miloStatus.currentExperiment.id
        ).catch(() => null);
        setMiloResults(experimentResults);
      } else {
        setMiloResults(null);
      }
      if (res.connected) setUseTribedDomain(res.managedDomain);
      setLocalPart((prev) =>
        prev || (res.managedDomain ? res.fromEmail?.split("@")[0] || "" : "")
      );
      setFromEmail((prev) => prev || res.fromEmail || "");
      setFromName((prev) => prev || res.fromName || "");
      setReplyTo((prev) => prev || res.replyTo || "");
      setDailyLimit((prev) =>
        prev || (res.dailyLimit != null ? String(res.dailyLimit) : "")
      );
      setPostalAddress((prev) => prev);
      if (res.connected) refreshCampaigns();
    } catch (e: any) {
      setError(e?.message || "Could not load email status");
    } finally {
      setLoading(false);
    }
  }, [communityId, refreshCampaigns]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onSaveSetup = async () => {
    if (!communityId) return;
    setSavingSetup(true);
    setError(null);
    setNotice(null);
    try {
      await setupEmailAutomation({
        communityId,
        useTribedDomain,
        localPart: useTribedDomain ? localPart.trim() || undefined : undefined,
        fromEmail: useTribedDomain ? undefined : fromEmail.trim(),
        fromName: fromName.trim() || undefined,
        replyTo: replyTo.trim() || undefined,
        dailyLimit: dailyLimit.trim() ? Number(dailyLimit) : null,
        postalAddress: postalAddress.trim() || undefined,
      });
      setNotice(
        useTribedDomain
          ? "Sender saved. The domain is set up automatically; verification usually completes within minutes."
          : "Sender saved."
      );
      setShowSenderSettings(false);
      refresh();
    } catch (e: any) {
      setError(e?.message || "Could not save the email setup");
    } finally {
      setSavingSetup(false);
    }
  };

  const onCheckDomain = async () => {
    if (!communityId) return;
    setCheckingDomain(true);
    setError(null);
    try {
      const res = await verifyEmailDomain(communityId);
      setStatus((prev) => (prev ? { ...prev, domain: res.domain } : prev));
      setNotice(
        res.domain.status === "verified"
          ? "Domain verified. You are ready to send."
          : `Domain status: ${res.domain.status}. DNS changes can take a while to propagate.`
      );
    } catch (e: any) {
      setError(e?.message || "Could not check the domain");
    } finally {
      setCheckingDomain(false);
    }
  };

  const onResumeMilo = async () => {
    if (!communityId) return;
    if (
      !window.confirm(
        "Resume this sender? Review the bounce, complaint, and unsubscribe cause first. Queued email will continue from where it paused."
      )
    ) {
      return;
    }
    setResumingMilo(true);
    setError(null);
    setNotice(null);
    try {
      await resumeMiloEmailSending(communityId);
      setNotice("Email sending resumed. Queued messages will continue safely.");
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Could not resume email sending");
    } finally {
      setResumingMilo(false);
    }
  };

  const onToggleSequence = async (type: SequenceType) => {
    if (seqType === type) {
      setSeqType(null);
      return;
    }
    if (!communityId) return;
    try {
      const res = await getEmailSequence(communityId, type);
      setSteps(
        res.steps.length > 0
          ? res.steps
          : [{ subject: "", body: "", delayDays: 0 }]
      );
      setWinbackEnabled(res.enabled !== false);
      setInactiveDays(String(res.inactiveDays || 30));
      setCooldownDays(String(res.cooldownDays || 90));
      setSeqType(type);
    } catch (e: any) {
      setError(e?.message || "Could not load the sequence");
    }
  };

  const updateStep = (i: number, patch: Partial<WelcomeStep>) => {
    setSteps((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s))
    );
  };

  const onSaveSequence = async () => {
    if (!communityId || !seqType) return;
    setSavingSequence(true);
    setError(null);
    setNotice(null);
    try {
      await saveEmailSequence({
        communityId,
        sequenceType: seqType,
        steps: steps.filter((s) => s.subject.trim() && s.body.trim()),
        ...(seqType === "winback"
          ? {
              enabled: winbackEnabled,
              inactiveDays: Number(inactiveDays) || 30,
              cooldownDays: Number(cooldownDays) || 90,
            }
          : {}),
      });
      setNotice(
        seqType === "welcome"
          ? "Welcome sequence saved. It applies to new signups."
          : "Winback sequence saved. It applies to members who go inactive."
      );
      refresh();
    } catch (e: any) {
      setError(e?.message || "Could not save the sequence");
    } finally {
      setSavingSequence(false);
    }
  };

  const onTestSend = async () => {
    if (!communityId) return;
    setTesting(true);
    setError(null);
    setNotice(null);
    try {
      const res = await sendTestEmail({
        communityId,
        subject: subject.trim(),
        body: body.trim(),
      });
      setNotice(`Test sent to ${res.sentTo}. Check your inbox before sending.`);
    } catch (e: any) {
      setError(e?.message || "Could not send the test email");
    } finally {
      setTesting(false);
    }
  };

  const onSend = async () => {
    if (!communityId) return;
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const res = await sendEmailCampaignNow({
        communityId,
        subject: subject.trim(),
        body: body.trim(),
      });
      setNotice(
        `Campaign queued for ${res.recipients} subscriber${
          res.recipients === 1 ? "" : "s"
        }. Delivery starts within minutes.`
      );
      setSubject("");
      setBody("");
      setConfirming(false);
      refresh();
      refreshCampaigns();
    } catch (e: any) {
      setError(e?.message || "Could not send the campaign");
    } finally {
      setSending(false);
    }
  };

  const onSendDraft = async (c: CampaignSummary) => {
    if (!communityId) return;
    if (
      !window.confirm(
        `Send "${c.subject || "this campaign"}" to every subscriber now?`
      )
    )
      return;
    setSendingId(c.id);
    setError(null);
    setNotice(null);
    try {
      const res = await sendDraftEmailCampaign(communityId, c.id);
      setNotice(
        `Campaign queued for ${res.recipients} subscriber${
          res.recipients === 1 ? "" : "s"
        }.`
      );
      refreshCampaigns();
    } catch (e: any) {
      setError(e?.message || "Could not send the campaign");
    } finally {
      setSendingId(null);
    }
  };

  const onDelete = async (c: CampaignSummary) => {
    if (!communityId) return;
    const label = c.subject || "this campaign";
    const warning =
      c.status === "sent"
        ? `Remove the record for "${label}"? This only deletes it from this list; the email was already sent.`
        : `Delete the draft "${label}"?`;
    if (!window.confirm(warning)) return;
    setDeletingId(c.id);
    setError(null);
    // Optimistic removal; refresh reconciles on failure.
    setCampaigns((prev) => prev.filter((x) => x.id !== c.id));
    try {
      await deleteEmailCampaign(communityId, c.id);
    } catch (e: any) {
      setError(e?.message || "Could not delete the campaign");
      refreshCampaigns();
    } finally {
      setDeletingId(null);
    }
  };

  const onImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!communityId || !file) return;
    setImporting(true);
    setError(null);
    setNotice(null);
    try {
      const subscribers = await parseSubscriberFile(file);
      if (!subscribers.length) {
        throw new Error(
          "No valid email addresses were found. Include an Email column, or put email addresses in a consistent column."
        );
      }
      if (
        !window.confirm(
          `Import ${subscribers.length} subscriber${
            subscribers.length === 1 ? "" : "s"
          } from ${file.name}? Only continue if these people consented to receive email. Existing, unsubscribed, and suppressed entries will be kept unchanged.`
        )
      ) {
        return;
      }
      let imported = 0;
      let existing = 0;
      let invalid = 0;
      for (let index = 0; index < subscribers.length; index += 400) {
        const result = await importEmailSubscribers(
          communityId,
          subscribers.slice(index, index + 400)
        );
        imported += result.imported;
        existing += result.existing;
        invalid += result.invalid;
      }
      setNotice(
        `Imported ${imported} subscriber${imported === 1 ? "" : "s"}. ${
          existing ? `${existing} existing entr${existing === 1 ? "y was" : "ies were"} kept. ` : ""
        }${invalid ? `${invalid} invalid row${invalid === 1 ? " was" : "s were"} skipped.` : ""}`.trim()
      );
      refresh();
    } catch (e: any) {
      setError(e?.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const canSend = Boolean(subject.trim() && body.trim());
  const domain = status?.domain || null;
  const domainVerified = domain?.status === "verified";
  const miloControl = miloResults?.variants.find((v) => v.key === "control");
  const miloCandidate = miloResults?.variants.find(
    (v) => v.key === "candidate"
  );
  const miloTaskStatus =
    milo?.latestTask?.status ||
    (milo?.currentExperiment ? "running" : "waiting");
  const miloPendingCount =
    milo?.agent?.attention?.pendingCount ?? milo?.communityPendingCount ?? 0;
  const miloPrimaryMetric =
    milo?.currentExperiment?.primaryMetric ||
    milo?.latestTask?.primaryMetric ||
    "plan_started";
  const miloControlRate =
    miloPrimaryMetric === "activation_first_action"
      ? miloControl?.activationRate
      : miloControl?.planStartRate;
  const miloCandidateRate =
    miloPrimaryMetric === "activation_first_action"
      ? miloCandidate?.activationRate
      : miloCandidate?.planStartRate;

  // The server computes the real managed address (coach-name subdomain with
  // collision handling); the local math is only a fallback while it loads.
  const suggestedDomain =
    status?.suggestedFromEmail?.split("@")[1] ||
    `${toLabel(communityId || "community")}.${TRIBED_MAIL_ROOT}`;
  const previewAddress = `${
    toLabel(localPart) ||
    status?.suggestedFromEmail?.split("@")[0] ||
    toLabel(fromName.split(/\s+/)[0] || "") ||
    "hello"
  }@${suggestedDomain}`;

  const senderForm = (
    <>
      <Row>
        <Field>
          <Label htmlFor="em-from-name">Sender name</Label>
          <Input
            id="em-from-name"
            placeholder="Coach name"
            value={fromName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFromName(e.target.value)
            }
          />
        </Field>
        {useTribedDomain ? (
          <Field>
            <Label htmlFor="em-local-part">Email handle</Label>
            <Input
              id="em-local-part"
              placeholder="coach"
              value={localPart}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLocalPart(e.target.value)
              }
            />
          </Field>
        ) : (
          <Field>
            <Label htmlFor="em-from-email">Sender email</Label>
            <Input
              id="em-from-email"
              placeholder="coach@mail.yourdomain.com"
              value={fromEmail}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFromEmail(e.target.value)
              }
            />
          </Field>
        )}
      </Row>
      {useTribedDomain && (
        <Muted>
          Emails will be sent from {previewAddress}. Domain setup and
          verification are handled automatically.
        </Muted>
      )}
      <ToggleButton
        type="button"
        onClick={() => setUseTribedDomain((v) => !v)}
      >
        {useTribedDomain
          ? "Use a custom domain instead"
          : "Use the Tribed email domain instead (no DNS setup)"}
      </ToggleButton>
      <ToggleButton
        type="button"
        onClick={() => setShowSetupAdvanced((v) => !v)}
      >
        {showSetupAdvanced ? "Hide advanced" : "Advanced"}
      </ToggleButton>
      {showSetupAdvanced && (
        <>
          <Row>
            <Field>
              <Label htmlFor="em-reply-to">
                Reply-to (the coach's real inbox)
              </Label>
              <Input
                id="em-reply-to"
                placeholder="coach@gmail.com"
                value={replyTo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setReplyTo(e.target.value)
                }
              />
            </Field>
            <Field>
              <Label htmlFor="em-daily-limit">Daily send cap (warmup)</Label>
              <Input
                id="em-daily-limit"
                placeholder="e.g. 200 for a new domain, empty for none"
                value={dailyLimit}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDailyLimit(e.target.value.replace(/[^0-9]/g, ""))
                }
              />
            </Field>
          </Row>
          <Row>
            <Field>
              <Label htmlFor="em-postal">
                Mailing address (shown in the footer, legally required for
                marketing email)
              </Label>
              <Input
                id="em-postal"
                placeholder="123 Main St, Austin, TX 78701"
                value={postalAddress}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPostalAddress(e.target.value)
                }
              />
            </Field>
          </Row>
        </>
      )}
    </>
  );

  return (
    <Wrap>
      <SectionHeader
        title="Email"
        description="Your own email service. New app members get the welcome sequence automatically; send the newsletter and campaigns to the whole list from here."
      />

      <MiloCard paused={milo?.sendingPaused}>
        <MiloHeader>
          <MiloMonogram>MI</MiloMonogram>
          <MiloIdentity>
            <strong>MILO</strong>
            <span>Monthly Iteration &amp; Lifecycle Optimizer</span>
          </MiloIdentity>
          <MiloPill
            tone={
              milo?.sendingPaused
                ? "warn"
                : miloTaskStatus === "running" ||
                  miloTaskStatus === "measuring" ||
                  miloTaskStatus === "completed"
                ? "good"
                : "neutral"
            }
          >
            {milo?.sendingPaused
              ? "Sender paused"
              : miloPendingCount > 0
              ? `${miloPendingCount} pending`
              : fmtMiloStatus(miloTaskStatus)}
          </MiloPill>
        </MiloHeader>

        <MiloCopy>
          MILO tests one welcome-email change at a time and judges it on real
          app activation. The queue lives in Tribed and can be processed by
          Claude, Codex, or any MCP-capable agent. Firebase checks
          deliverability every day.
        </MiloCopy>

        {milo?.agent?.notificationEmail ? (
          <MiloCopy>
            <strong>Weekly intelligence:</strong> Every community admin gets a
            private report with 7-day app analytics, MILO, Funnel, NUDGE, SOLO,
            LENS, running experiments, and prioritized improvements.
            Portfolio oversight is copied to {milo.agent.notificationEmail}.
          </MiloCopy>
        ) : miloPendingCount > 0 ? (
          <Warn>
            {miloPendingCount} task{miloPendingCount === 1 ? "" : "s"} need
            attention. Digital University platform admins receive the weekly
            intelligence brief automatically.
          </Warn>
        ) : null}

        <MiloGrid>
          <MiloMetric>
            <span>Current cycle</span>
            <strong>{milo?.latestTask?.cycle || "Waiting for sync"}</strong>
          </MiloMetric>
          <MiloMetric>
            <span>Primary signal</span>
            <strong>{fmtMiloStatus(miloPrimaryMetric)}</strong>
          </MiloMetric>
          <MiloMetric>
            <span>Control activation</span>
            <strong>
              {miloControl
                ? `${fmtPercent(miloControlRate)} · ${
                    miloControl.enrolled
                  } enrolled`
                : "No evidence yet"}
            </strong>
          </MiloMetric>
          <MiloMetric>
            <span>Candidate activation</span>
            <strong>
              {miloCandidate
                ? `${fmtPercent(miloCandidateRate)} · ${
                    miloCandidate.enrolled
                  } enrolled`
                : "No evidence yet"}
            </strong>
          </MiloMetric>
        </MiloGrid>

        {(milo?.currentExperiment?.hypothesis ||
          milo?.latestTask?.hypothesis) && (
          <MiloCopy>
            <strong>Hypothesis:</strong>{" "}
            {milo?.currentExperiment?.hypothesis ||
              milo?.latestTask?.hypothesis}
          </MiloCopy>
        )}

        {milo?.latestTask?.blockedReasons?.length ? (
          <Warn>
            Waiting on:{" "}
            {milo.latestTask.blockedReasons
              .map((reason) => fmtMiloStatus(reason))
              .join(", ")}
          </Warn>
        ) : null}

        {milo?.sendingPaused && (
          <>
            <Warn>
              {milo.pauseReason ||
                "MILO paused this sender after a deliverability guardrail was breached."}
            </Warn>
            <Row>
              <Button
                variant="ghost"
                size="sm"
                onClick={onResumeMilo}
                disabled={resumingMilo}
              >
                {resumingMilo ? "Resuming..." : "Review complete — resume"}
              </Button>
            </Row>
          </>
        )}
      </MiloCard>

      {loading && !status ? (
        <Muted>Loading...</Muted>
      ) : !status?.connected ? (
        <>
          <Intro>
            Set up this community's sender identity to start building its email
            list. The Tribed email domain works out of the box with no DNS
            setup; a coach with their own domain can switch to it anytime.
          </Intro>
          {senderForm}
          <Row>
            <Button
              variant="primary"
              onClick={onSaveSetup}
              disabled={
                savingSetup ||
                !communityId ||
                (!useTribedDomain && !fromEmail.trim())
              }
            >
              {savingSetup ? "Setting up..." : "Set up email"}
            </Button>
          </Row>
        </>
      ) : (
        <>
          <StatusLine>
            <Dot on={domainVerified} />
            <span>
              {status.fromName ? `${status.fromName} ` : ""}
              &lt;{status.fromEmail}&gt;
            </span>
            <Muted>
              {status.subscriberCount != null
                ? `${status.subscriberCount} subscriber${
                    status.subscriberCount === 1 ? "" : "s"
                  }`
                : ""}
            </Muted>
            {status.queuedCount ? (
              <Muted>{status.queuedCount} queued</Muted>
            ) : null}
            {status.dailyLimit ? (
              <Muted>cap {status.dailyLimit}/day</Muted>
            ) : null}
            {status.suppressedCount ? (
              <Muted>{status.suppressedCount} suppressed</Muted>
            ) : null}
            <Muted>
              {status.welcomeStepCount} welcome email
              {status.welcomeStepCount === 1 ? "" : "s"}
            </Muted>
          </StatusLine>

          {domain && !domainVerified && status.managedDomain && (
            <DnsBox>
              <Warn>
                The sender domain {domain.name} is being verified (status:{" "}
                {domain.status}). DNS was configured automatically; this
                usually completes within minutes.
              </Warn>
              <Row>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCheckDomain}
                  disabled={checkingDomain}
                >
                  {checkingDomain ? "Checking..." : "Check verification"}
                </Button>
              </Row>
            </DnsBox>
          )}

          {domain && !domainVerified && !status.managedDomain && (
            <DnsBox>
              <Warn>
                The sender domain {domain.name} is not verified yet (status:{" "}
                {domain.status}). Add these DNS records at the domain's
                registrar, then check again. Emails will not deliver reliably
                until this is done.
              </Warn>
              <DnsTable>
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Value</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domain.records.map((r, i) => (
                      <tr key={i}>
                        <td>{r.type}</td>
                        <td title={r.name}>{r.name}</td>
                        <td title={r.value}>{r.value}</td>
                        <td>{r.status || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DnsTable>
              <Row>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCheckDomain}
                  disabled={checkingDomain}
                >
                  {checkingDomain ? "Checking..." : "Check verification"}
                </Button>
              </Row>
            </DnsBox>
          )}

          <Row>
            <ToggleButton
              type="button"
              onClick={() => onToggleSequence("welcome")}
            >
              {seqType === "welcome"
                ? "Hide welcome sequence"
                : "Edit welcome sequence"}
            </ToggleButton>
            <ToggleButton
              type="button"
              onClick={() => onToggleSequence("winback")}
            >
              {seqType === "winback"
                ? "Hide winback sequence"
                : "Edit winback sequence"}
            </ToggleButton>
            <ToggleButton
              type="button"
              onClick={() => setShowSenderSettings((v) => !v)}
            >
              {showSenderSettings ? "Hide sender settings" : "Sender settings"}
            </ToggleButton>
            <ImportLabel $disabled={importing}>
              {importing ? "Importing subscribers..." : "Import subscribers from CSV or Excel"}
              <input
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                disabled={importing}
                onChange={onImport}
              />
            </ImportLabel>
          </Row>

          {showSenderSettings && (
            <>
              {senderForm}
              <Row>
                <Button
                  variant="primary"
                  onClick={onSaveSetup}
                  disabled={
                    savingSetup || (!useTribedDomain && !fromEmail.trim())
                  }
                >
                  {savingSetup ? "Saving..." : "Save sender"}
                </Button>
              </Row>
            </>
          )}

          {seqType && (
            <>
              <Intro>
                {seqType === "welcome"
                  ? "These emails go out automatically to every new signup. Changes apply to future signups."
                  : "These emails go out automatically when a member stops using the app, to win them back. If they return mid-sequence, remaining emails are cancelled."}{" "}
                Use {"{{firstName}}"} to personalize. HTML works.
              </Intro>
              {seqType === "winback" && (
                <Row>
                  <Field style={{ flex: "0 0 auto" }}>
                    <Label>Days inactive before it starts</Label>
                    <SmallInput
                      value={inactiveDays}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setInactiveDays(e.target.value.replace(/[^0-9]/g, ""))
                      }
                    />
                  </Field>
                  <Field style={{ flex: "0 0 auto" }}>
                    <Label>Cooldown between rounds (days)</Label>
                    <SmallInput
                      value={cooldownDays}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCooldownDays(e.target.value.replace(/[^0-9]/g, ""))
                      }
                    />
                  </Field>
                  <Field style={{ flex: "0 0 auto" }}>
                    <Label htmlFor="em-wb-enabled">Active</Label>
                    <input
                      id="em-wb-enabled"
                      type="checkbox"
                      checked={winbackEnabled}
                      onChange={(e) => setWinbackEnabled(e.target.checked)}
                    />
                  </Field>
                </Row>
              )}
              {steps.map((step, i) => (
                <StepCard key={i}>
                  <StepHead>
                    <span>Email {i + 1}</span>
                    <DeleteButton
                      type="button"
                      onClick={() =>
                        setSteps((prev) => prev.filter((_, idx) => idx !== i))
                      }
                      disabled={steps.length <= 1}
                    >
                      Remove
                    </DeleteButton>
                  </StepHead>
                  {i > 0 && (
                    <Row>
                      <Field style={{ flex: "0 0 auto" }}>
                        <Label>Days after previous email</Label>
                        <SmallInput
                          value={String(step.delayDays)}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateStep(i, {
                              delayDays: Number(
                                e.target.value.replace(/[^0-9]/g, "") || 0
                              ),
                            })
                          }
                        />
                      </Field>
                    </Row>
                  )}
                  <Field>
                    <Label>Subject</Label>
                    <Input
                      value={step.subject}
                      placeholder="Welcome to the community"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateStep(i, { subject: e.target.value })
                      }
                    />
                  </Field>
                  <Field>
                    <Label>Body</Label>
                    <Textarea
                      rows={6}
                      value={step.body}
                      placeholder="Plain text or HTML."
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        updateStep(i, { body: e.target.value })
                      }
                    />
                  </Field>
                </StepCard>
              ))}
              <Row>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setSteps((prev) => [
                      ...prev,
                      { subject: "", body: "", delayDays: 2 },
                    ])
                  }
                >
                  Add email
                </Button>
                <Button
                  variant="primary"
                  onClick={onSaveSequence}
                  disabled={savingSequence}
                >
                  {savingSequence ? "Saving..." : "Save sequence"}
                </Button>
              </Row>
            </>
          )}

          <SectionHeader title="Send an email campaign" />
          <Field>
            <Label htmlFor="em-subject">Subject</Label>
            <Input
              id="em-subject"
              placeholder="This week in the community"
              value={subject}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSubject(e.target.value)
              }
            />
          </Field>
          <Field>
            <Label htmlFor="em-body">Message</Label>
            <Textarea
              id="em-body"
              rows={8}
              placeholder="Write your update. Plain text or HTML both work."
              value={body}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setBody(e.target.value)
              }
            />
          </Field>

          {confirming ? (
            <Confirm>
              <span>
                Send "{subject.trim()}" to{" "}
                {status.subscriberCount != null
                  ? `${status.subscriberCount} subscriber${
                      status.subscriberCount === 1 ? "" : "s"
                    }`
                  : "every subscriber"}{" "}
                now?
              </span>
              <Button variant="primary" onClick={onSend} disabled={sending}>
                {sending ? "Sending..." : "Yes, send now"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirming(false)}
                disabled={sending}
              >
                Cancel
              </Button>
            </Confirm>
          ) : (
            <Row>
              <Button
                variant="primary"
                onClick={() => {
                  setNotice(null);
                  setConfirming(true);
                }}
                disabled={!canSend}
              >
                Send campaign
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onTestSend}
                disabled={!canSend || testing}
                title="Send this email to yourself first"
              >
                {testing ? "Sending test..." : "Send me a test"}
              </Button>
            </Row>
          )}

          <SectionHeader title="Campaigns" />
          {campaigns.length === 0 ? (
            <Empty>No campaigns yet. Send one above.</Empty>
          ) : (
            <List>
              {campaigns.map((c) => (
                <ListRow key={c.id}>
                  <Subject>{c.subject || <Muted>No subject</Muted>}</Subject>
                  <StatusTag sent={c.status === "sent"}>
                    {c.status === "sent" ? "Sent" : "Draft"}
                  </StatusTag>
                  <Muted>
                    {c.recipients != null ? `${c.recipients} sent` : "—"}
                    {c.openCount != null && c.openCount > 0
                      ? ` · ${c.openCount} opens`
                      : ""}
                  </Muted>
                  <Muted>{fmtCampaignDate(c.sentAt || c.createdAt)}</Muted>
                  {c.status === "sent" ? (
                    <span />
                  ) : (
                    <RowSendButton
                      type="button"
                      onClick={() => onSendDraft(c)}
                      disabled={sendingId === c.id || deletingId === c.id}
                      title="Send this campaign now"
                    >
                      {sendingId === c.id ? "Sending…" : "Send"}
                    </RowSendButton>
                  )}
                  <DeleteButton
                    type="button"
                    onClick={() => onDelete(c)}
                    disabled={deletingId === c.id || sendingId === c.id}
                    aria-label="Delete campaign"
                    title="Delete campaign"
                  >
                    {deletingId === c.id ? "…" : "Delete"}
                  </DeleteButton>
                </ListRow>
              ))}
            </List>
          )}
        </>
      )}

      {error && <Warn>{error}</Warn>}
      {notice && <Notice>{notice}</Notice>}
    </Wrap>
  );
};

export default EmailAutomationPanel;
